const CONFIG = {
   MAX_FILENAME_LENGTH: 150,
   MAX_FILTER_PARTS: 5,
   MAX_PART_LENGTH: 25,
};

const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;

const RESERVED_NAMES = [
   'CON',
   'PRN',
   'AUX',
   'NUL',
   'COM1',
   'COM2',
   'COM3',
   'COM4',
   'COM5',
   'COM6',
   'COM7',
   'COM8',
   'COM9',
   'LPT1',
   'LPT2',
   'LPT3',
   'LPT4',
   'LPT5',
   'LPT6',
   'LPT7',
   'LPT8',
   'LPT9',
];

interface FilterConfig {
   key: string;
   prefix: string;
   maxItems?: number;
   priority: number;
}

const FILTER_CONFIGS: FilterConfig[] = [
   { key: 'itemNos', prefix: 'Items', maxItems: 2, priority: 1 },
   { key: 'hfbs', prefix: 'HFB', maxItems: 2, priority: 1 },
   { key: 'pas', prefix: 'PA', maxItems: 1, priority: 1 },
   { key: 'familyName', prefix: 'Family', maxItems: 1, priority: 1 },
   { key: 'itemState', prefix: 'State', maxItems: 1, priority: 3 },
   { key: 'itemName', prefix: 'Name', priority: 2 },
   { key: 'search', prefix: 'Search', priority: 2 },
   { key: 'part', prefix: 'Part', priority: 2 },
   { key: 'partDetail', prefix: 'Detail', priority: 2 },
   { key: 'materialGradeId', prefix: 'Grade', priority: 2 },
   { key: 'search2', prefix: 'MatGrade', priority: 2 },
   { key: 'comment', prefix: 'Comment', priority: 3 },
   { key: 'updatedBy', prefix: 'By', priority: 3 },
   { key: 'buCodePru', prefix: 'PRU', priority: 3 },
   { key: 'buCodePdu', prefix: 'PDU', priority: 3 },
   { key: 'progressState', prefix: 'Progress', priority: 3 },
   { key: 'piaImportStatus', prefix: 'PIA', priority: 3 },
   { key: 'ssd', prefix: 'SSD', priority: 3 },
   { key: 'eds', prefix: 'EDS', priority: 3 },
];

const BOOLEAN_FILTERS: Record<string, { text: string; priority: number }> = {
   locked: { text: 'Locked', priority: 3 },
   unlocked: { text: 'Unlocked', priority: 3 },
   dwpDeviates: { text: 'DWP', priority: 3 },
   isBase: { text: 'Base', priority: 3 },
   excludeConnected: { text: 'NoConn', priority: 3 },
   missingGradeId: { text: 'NoGrade', priority: 3 },
};

const sanitizeFilename = (filename: string): string => {
   let sanitized = filename.replace(INVALID_FILENAME_CHARS, '-');

   sanitized = sanitized.replace(/-+/g, '-');

   sanitized = sanitized.trim().replace(/^-+|-+$/g, '');

   if (!sanitized) {
      return 'export';
   }

   const nameWithoutExt = sanitized.replace(/\.[^.]+$/, '');
   if (RESERVED_NAMES.includes(nameWithoutExt.toUpperCase())) {
      sanitized = `export_${sanitized}`;
   }

   return sanitized;
};

const sanitizeFilterValue = (str: string): string => {
   return str
      .replace(/[^a-zA-Z0-9-_]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, CONFIG.MAX_PART_LENGTH);
};

const buildFilterParts = (filters: any): Array<{ text: string; priority: number }> => {
   const parts: Array<{ text: string; priority: number }> = [];

   FILTER_CONFIGS.forEach(({ key, prefix, maxItems, priority }) => {
      const value = filters[key];

      if (Array.isArray(value) && value.length > 0) {
         const items = value.slice(0, maxItems || 1).join('-');
         const more = value.length > (maxItems || 1) ? `+${value.length - (maxItems || 1)}` : '';
         parts.push({ text: `${prefix}-${sanitizeFilterValue(items)}${more}`, priority });
      } else if (typeof value === 'string' && value.trim()) {
         parts.push({ text: `${prefix}-${sanitizeFilterValue(value)}`, priority });
      }
   });

   Object.entries(BOOLEAN_FILTERS).forEach(([key, { text, priority }]) => {
      if (filters[key]) {
         parts.push({ text, priority });
      }
   });

   return parts;
};

export const getFilterSuffix = (filters: any): string => {
   if (!filters) return '';
   const parts = buildFilterParts(filters);
   parts.sort((a, b) => a.priority - b.priority);

   const totalParts = parts.length;
   const selectedParts = parts.slice(0, CONFIG.MAX_FILTER_PARTS);
   const remainingCount = totalParts - CONFIG.MAX_FILTER_PARTS;

   let suffix = selectedParts.map((p) => p.text).join('_');
   if (remainingCount > 0) {
      suffix += `_+${remainingCount}more`;
   }

   return suffix.length > 0 ? `_${suffix}` : '';
};

export const generateExportFilename = (
   baseFilename: string = 'material_export',
   filters?: any,
   includeTimestamp: boolean = true
): string => {
   let filename = sanitizeFilename(baseFilename);

   let timestamp = '';
   if (includeTimestamp) {
      timestamp = `_${new Date().toISOString().slice(0, 10)}`;
   }

   const extension = '.xlsx';
   const baseLength = filename.length + timestamp.length + extension.length;
   const availableSpace = CONFIG.MAX_FILENAME_LENGTH - baseLength;

   if (filters && Object.keys(filters).length > 0) {
      let filterSuffix = getFilterSuffix(filters);

      if (filterSuffix.length > availableSpace) {
         filterSuffix = filterSuffix.substring(0, availableSpace - 3) + '...';
      }

      filename = sanitizeFilename(filename + filterSuffix);
   }

   filename = sanitizeFilename(filename + timestamp);

   return filename + extension;
};
